/**
 * Project-value detection: infer how much the project at hand matters from
 * observable context — repo scale, stack breadth, criticality language in the
 * request/memory, request scope — and emit a {@link ValueAssessment} the budget
 * composer (budget.ts) turns into a token/depth allowance.
 *
 * Deliberately a deterministic heuristic (no model call): it must be cheap,
 * testable, and explainable. Every contributing signal is recorded so the
 * assessment is auditable, and the user can override the tier outright
 * (`--value <tier>`), so a misjudgment can't silently burn tokens.
 */
import type { RepoCard } from '@fim/futurex-protocol';
import type { PlanningConfig, ValueTier } from './planning-config.js';

export interface ValueSignal {
  readonly name: string;
  readonly detail: string;
  /** Signed contribution to the score. */
  readonly delta: number;
}

export interface ValueAssessment {
  readonly tier: ValueTier;
  /** 0..1; thresholds in config map it to the tier. */
  readonly score: number;
  readonly signals: readonly ValueSignal[];
  /** Set when the user forced the tier (score/signals kept for transparency). */
  readonly overridden?: boolean;
}

export interface ValueContext {
  readonly card: RepoCard | null;
  /** Retrieval-index chunk count, when an index exists. */
  readonly indexedChunks?: number;
  /** The user's request — scanned for criticality/scope language. */
  readonly task: string;
  /** Project memory contents, when present (also scanned). */
  readonly memory?: string | null;
}

/** Baseline score for a project with no signals either way ("standard"). */
const BASELINE_SCORE = 0.4;

const CRITICAL_WORDS = [
  'production', 'critical', 'payment', 'billing', 'auth', 'authentication',
  'authorization', 'security', 'compliance', 'customers', 'revenue', 'medical',
  'finance', 'launch',
];
const LOW_WORDS = [
  'prototype', 'throwaway', 'experiment', 'demo', 'toy', 'scratch',
  'poc', 'proof of concept', 'hackathon', 'quick test',
];

/** Whole-word match — 'poc' must not fire on 'epoch', nor 'auth' on 'author'. */
function mentions(text: string, phrase: string): boolean {
  const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
  return new RegExp(`\\b${escaped}\\b`, 'i').test(text);
}

function clamp01(x: number): number {
  return Math.min(1, Math.max(0, x));
}

/** Assess project value from context. Pure given its inputs + config. */
export function assessProjectValue(input: ValueContext, cfg: PlanningConfig): ValueAssessment {
  const v = cfg.value;
  const signals: ValueSignal[] = [];

  // Repo scale — a larger indexed codebase implies more invested (and at-risk) work.
  const chunks = input.indexedChunks ?? 0;
  if (chunks >= v.repoLargeChunks) {
    signals.push({ name: 'repo-scale', detail: `${chunks} indexed chunks (large)`, delta: v.weights.repoLarge });
  } else if (chunks >= v.repoMediumChunks) {
    signals.push({ name: 'repo-scale', detail: `${chunks} indexed chunks (medium)`, delta: v.weights.repoMedium });
  }

  // Stack breadth — multiple detected facets (framework, backend, orm, tests)
  // means a real, multi-layer system rather than a single script.
  const facets = [input.card?.framework, input.card?.backend, input.card?.orm, input.card?.test].filter(Boolean).length;
  if (facets >= 2) {
    signals.push({ name: 'stack-breadth', detail: `${facets} stack facets detected`, delta: v.weights.stackBreadth });
  }

  // Criticality language in the request + project memory.
  const text = `${input.task}\n${input.memory ?? ''}`.toLowerCase();
  const critHits = CRITICAL_WORDS.filter((w) => mentions(text, w));
  if (critHits.length > 0) {
    signals.push({
      name: 'criticality',
      detail: `mentions: ${critHits.slice(0, 4).join(', ')}`,
      delta: v.weights.criticalKeyword,
    });
  }
  const lowHits = LOW_WORDS.filter((w) => mentions(text, w));
  if (lowHits.length > 0) {
    signals.push({
      name: 'low-stakes',
      detail: `mentions: ${lowHits.slice(0, 4).join(', ')}`,
      delta: v.weights.lowKeyword,
    });
  }

  // Request scope — long, multi-part requests imply a broader change surface.
  const parts = input.task.split(/\n|(?:^|\s)(?:\d+[.)]|[-*])\s/).filter((s) => s.trim().length > 20).length;
  if (parts >= 3 || input.task.length > 600) {
    signals.push({ name: 'broad-scope', detail: `multi-part request (${parts} parts, ${input.task.length} chars)`, delta: v.weights.broadScope });
  }

  const score = clamp01(BASELINE_SCORE + signals.reduce((sum, s) => sum + s.delta, 0));
  let tier: ValueTier;
  if (score >= v.criticalAtLeast) tier = 'critical';
  else if (score >= v.highAtLeast) tier = 'high';
  else if (score < v.lowBelow) tier = 'low';
  else tier = 'standard';

  return { tier, score, signals };
}

/** Apply a user override on top of a computed assessment (kept auditable). */
export function overrideValueTier(assessment: ValueAssessment, tier: ValueTier): ValueAssessment {
  return { ...assessment, tier, overridden: true };
}

/** Render the assessment for plan output. */
export function formatValue(a: ValueAssessment, cfg: PlanningConfig): string {
  const mult = cfg.valueTiers[a.tier].multiplier;
  const why = a.signals.length > 0 ? a.signals.map((s) => s.detail).join(' · ') : 'no strong signals';
  return `value: ${a.tier}${a.overridden ? ' (user override)' : ''} ×${mult} — ${why}`;
}
